// Module: apiErrorUtils.js
// Purpose: Shared helpers for extracting structured backend error payloads from
//          rejected API errors. R67: surface the R45 deploy-gate allocation
//          rejection (code + message) instead of a generic axios status string.
//          The shared axios instance (frontend/src/services/api.js:101-118)
//          rewrites every rejected error via handleResponseError, so the
//          post-interceptor shape is the primary path; raw-axios shapes are
//          retained as defensive fallbacks for any non-interceptor caller.
//          Also exports extractDriftConflict: classifies a 409
//          DEPLOY_DRIFT_UNRESOLVED from the preserved `err.response`
//          (services/api.js:145-152) because it needs the HTTP status.

/**
 * Returns true only when `candidate` is an object carrying both a truthy
 * string `code` and a truthy string `message`. Guards against the case where
 * the interceptor placed a plain axios string into `.message`
 * (`"".error` is undefined → not an object → null), so the caller falls back
 * to the existing generic error path. Never throws.
 *
 * @param {*} candidate
 * @returns {boolean}
 */
function isStructuredError(candidate) {
  return (
    !!candidate &&
    typeof candidate === 'object' &&
    typeof candidate.code === 'string' &&
    candidate.code.length > 0 &&
    typeof candidate.message === 'string' &&
    candidate.message.length > 0
  );
}

/**
 * Extract a structured `{ code, message }` allocation-rejection payload from a
 * rejected API error, or `null` when no structured shape is present.
 *
 * Precedence (first match wins):
 *   1. err.message.error                  — post-interceptor shape
 *      (services/api.js:110 assigns the FastAPI `detail` object verbatim
 *      onto errorResponse.message; the payload lives at .message.error)
 *   2. err.response.data.detail.error     — raw-axios shape (defensive:
 *      direct-axios / non-interceptor future caller)
 *   3. err.response.data.error            — raw-axios shape without the
 *      FastAPI `detail` wrapper (defensive)
 *   4. else → null
 *
 * A candidate qualifies only when it has BOTH a truthy string `code` and a
 * truthy string `message`. Defensive: never throws on null / undefined /
 * missing / malformed input — returns `null` instead.
 *
 * @param {*} err - the caught (post-interceptor or raw) error.
 * @returns {{ code: string, message: string } | null}
 */
export function extractAllocationError(err) {
  if (!err || typeof err !== 'object') {
    return null;
  }

  //--> 1. post-interceptor shape: interceptor moved the FastAPI `detail`
  //       object onto err.message, so the payload is at err.message.error
  const postInterceptor = err.message;
  if (postInterceptor && typeof postInterceptor === 'object') {
    const candidate = postInterceptor.error;
    if (isStructuredError(candidate)) {
      return { code: candidate.code, message: candidate.message };
    }
  }

  //--> 2/3. raw-axios fallbacks (defensive: caller bypassed the interceptor)
  const data = err.response && err.response.data;
  if (data && typeof data === 'object') {
    const detailError = data.detail && data.detail.error;
    if (isStructuredError(detailError)) {
      return { code: detailError.code, message: detailError.message };
    }

    const directError = data.error;
    if (isStructuredError(directError)) {
      return { code: directError.code, message: directError.message };
    }
  }

  //--> No structured allocation shape — caller uses the generic fallback.
  return null;
}

/**
 * Extract the drift-conflict payload from a rejected deploy call, or `null`.
 * Matches only a 409 whose `detail.error.code` is DEPLOY_DRIFT_UNRESOLVED AND
 * whose `details.diff_snapshot_hash` is a non-empty string (a hash-less 409
 * cannot be resolved, so it falls through to the generic error toast).
 * Reads the preserved `err.response` (services/api.js:145-152). Never throws.
 *
 * @param {*} err - the caught (post-interceptor or raw) error.
 * @returns {{ diffSnapshotHash: string, divergedAccounts: Array } | null}
 */
export function extractDriftConflict(err) {
  const response = err && typeof err === 'object' ? err.response : null;
  if (!response || response.status !== 409) return null;
  const body = response.data && response.data.detail && response.data.detail.error;
  if (!body || body.code !== 'DEPLOY_DRIFT_UNRESOLVED') return null;
  const details = body.details;
  const hash = details && details.diff_snapshot_hash;
  //* A 409 without diff_snapshot_hash cannot be resolved (no hash to resubmit), so it fails loud
  //* via the generic error path — the design's 'Refine R1 AC' (test_extractDriftConflict_null_when_hash_missing).
  if (typeof hash !== 'string' || hash.length === 0) return null;
  return {
    diffSnapshotHash: hash,
    divergedAccounts: Array.isArray(details.diverged_accounts) ? details.diverged_accounts : [],
  };
}
